import { T } from '@tolgee/react';
import { Modal } from 'antd';
import { useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import { ArrowDownSvg } from '../../../assets/icons';
import AppreciateSvg from '../../../assets/icons/AppreciateSvg';
import FoldedHandsSvg from '../../../assets/icons/FoldedHandsSvg';
import StarStruckSvg from '../../../assets/icons/StarStruckSvg';
import theme from '../../../assets/theme';
import { AuthError } from '../../../types/auth';
import { Button } from '../../_atoms/Button';

const NiceUserError = ({
  error,
  handleSignIn,
  handleSignUp,
  handleClose,
}: {
  error: AuthError | undefined;
  handleSignIn: () => void;
  handleSignUp: () => void;
  handleClose: () => void;
}) => {
  if (!error) return null;

  switch (error.type) {
    case 'accountAlreadyExists':
      return (
        <ErrorWrapper>
          <StarStruckSvg />
          <h2>
            <T
              keyName="Oops! You already signed up."
              defaultValue="Oops! You already signed up."
            />
          </h2>
          <span>
            <T
              keyName="Great to see you again! It looks like an account has already been created with
            this email address. Go ahead and log in."
              defaultValue="Great to see you again! It looks like an account has already been created with
            this email address. Go ahead and log in."
            />
          </span>
          <Button onClick={handleSignIn}>
            <T keyName="Log in" defaultValue="Log in" />
          </Button>
        </ErrorWrapper>
      );

    case 'doesNotMatchRules':
      return (
        <ErrorWrapper>
          <FoldedHandsSvg />
          <h2>
            <T
              keyName="We can't find your your email domain"
              defaultValue="We can't find your your email domain."
            />
          </h2>
          <span>
            <T
              keyName="You may have used another email address to sign up Please try signing in with
            a different email"
              defaultValue="You may have used another email address to sign up. Please try signing in with
            a different email."
            />
          </span>
          <Button onClick={handleSignIn}>
            <T keyName="Try again" defaultValue="Try again" />
          </Button>
        </ErrorWrapper>
      );

    case 'noAccountFound':
      return (
        <ErrorWrapper>
          <AppreciateSvg />
          <h2>
            <T
              keyName="We can't find an account for you"
              defaultValue="We can't find an account for you"
            />
          </h2>
          <span>
            <T
              keyName="If your school has purchased Curipod, look for an email invite from your
            admin Otherwise, you can create a new account now"
              defaultValue="If your school has purchased Curipod, look for an email invite from your
            admin. Otherwise, you can create a new account now."
            />
          </span>
          <Button onClick={handleSignUp}>
            <T keyName="Sign up now" defaultValue="Sign up now" />
          </Button>
        </ErrorWrapper>
      );

    case 'unknown':
    default:
      return (
        <ErrorWrapper>
          <FoldedHandsSvg />
          <h2>
            <T
              keyName="Oh no! An unexpected error occurred."
              defaultValue="Oh no! An unexpected error occurred."
            />
          </h2>
          <span>
            {error.message ?? (
              <T
                keyName="We're very sorry A notification has been sent to our team Please try again"
                defaultValue="We're very sorry. A notification has been sent to our team. Please try again."
              />
            )}
          </span>
          <Button onClick={handleClose}>
            <T keyName="Try again" defaultValue="Try again" />
          </Button>
        </ErrorWrapper>
      );
  }
};

export const UserNotFoundModal = ({
  error,
  onSignIn,
  onSignUp,
  onClose,
  visible,
}: {
  error: AuthError | undefined;
  visible: boolean;
  onClose: () => void;
  onSignIn: () => void;
  onSignUp: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { showNewMessages } = useIntercom();

  if (!error) return null;

  const handleSignIn = () => {
    onSignIn();
  };
  const handleSignUp = () => {
    onSignUp();
  };
  const handleClose = () => {
    onClose();
  };

  const handleContactSupport = () => {
    // Map error types to user-friendly strings
    const errorTypeMap = {
      noAccountFound: 'Account not found',
      accountAlreadyExists: 'Account already exists',
      doesNotMatchRules: 'Email domain does not match organization rules',
      unknown: 'Unknown authentication error',
    };

    const friendlyErrorType = errorTypeMap[error.type] || error.type;

    const message = `Hello! I'm experiencing an authentication error and need help.

Error: ${friendlyErrorType}
${error.correlationId ? `Correlation ID: ${error.correlationId}` : ''}
${error.message ? `Additional details: ${error.message}` : ''}

Please help me resolve this issue.`;

    showNewMessages(message);
  };

  return (
    <StyledModal visible={visible} footer={false} closable={false}>
      <NiceUserError
        error={error}
        handleClose={handleClose}
        handleSignIn={handleSignIn}
        handleSignUp={handleSignUp}
      />
      {isExpanded && error.correlationId && (
        <p>Correlation ID for support: {error.correlationId}</p>
      )}
      {isExpanded && (
        <ExpandedErrorMsg>
          <T
            keyName="If you feel this is a mistake, please"
            defaultValue="If you feel this is a mistake, please"
          />{' '}
          <ContactSupportLink onClick={handleContactSupport}>
            <T keyName="Contact support" defaultValue="Contact support" />
          </ContactSupportLink>
          .
        </ExpandedErrorMsg>
      )}

      <ViewMoreContainer
        $isActive={isExpanded}
        role="button"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>
          <T keyName="Expand full message" defaultValue="Expand full message" />
        </span>
        <ArrowDownSvg />
      </ViewMoreContainer>
    </StyledModal>
  );
};

const ErrorWrapper = styled.span`
  text-align: center;
  display: inline-grid;

  h2 {
    font-weight: bold;
  }

  button {
    place-self: center;
    font-size: 14px;
    margin-top: 40px;
    margin-bottom: 20px;
  }

  svg {
    place-self: center;
    width: 100px;
    height: auto;
    margin-bottom: 10px;
  }
`;

const ExpandedErrorMsg = styled.p`
  text-align: center;
  font-size: 12px;
`;

const ViewMoreContainer = styled.div<{ $isActive?: boolean }>`
  font-weight: bold;
  justify-content: center;
  font-size: 12px;
  color: ${theme.colors.text.grey};
  display: flex;
  align-items: center;
  cursor: pointer;
  span {
    margin-left: 5px;
  }
  svg {
    transition: all 300ms cubic-bezier(0.645, 0.045, 0.355, 1);
    color: ${theme.colors.text.grey};
    margin-left: 5px;
    width: 8px;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `};
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 10px;
  }

  .ant-modal-body {
    text-align: center;
  }
`;

const ContactSupportLink = styled.span`
  color: ${theme.colors.blue};
  cursor: pointer;
  text-decoration: underline;
`;

export default UserNotFoundModal;
